import React from 'react';
import PropTypes from 'prop-types';
import WaitingIndicator from '../WaitingIndicator/WaitingIndicator';
import { Link } from 'react-router-dom';
import { FaChevronLeft } from 'react-icons/fa';
import './EntityDetailLayout.css';

/**
 * EntityDetailLayout
 * 
 * A standardized layout wrapper for all entity detail pages (Days, Places, Travel, Accommodation).
 * Enforces a consistent visual hierarchy while providing slots for specific content.
 * 
 * @param {Object} props
 * @param {string} props.title - Main entity title (e.g., "Day 1", "Paris")
 * @param {React.ReactNode} [props.subtitle] - Secondary info (e.g., Date, Address)
 * @param {React.ReactNode} [props.icon] - Entity type icon
 * @param {string} [props.backLink] - URL for the back button
 * @param {string} [props.backText] - Text for the back button
 * @param {React.ReactNode[]} [props.primaryActions] - Major actions (Edit, Upload)
 * @param {React.ReactNode[]} [props.secondaryActions] - Minor actions (Link, Unlink)
 * @param {React.ReactNode} props.mainContent - The core unique content for this entity
 * @param {React.ReactNode} [props.resourcesContent] - Dedicated slot for Resources (Default, External, AI)
 * @param {React.ReactNode} [props.contextContent] - Sidebar or extra context info
 * @param {React.ReactNode} [props.relatedContent] - Related Entities/Documents/Days section
 * @param {React.ReactNode} [props.notesContent] - Bottom notes section
 * @param {boolean} [props.loading] - Loading state
 * @param {string} [props.error] - Error message
 * @param {string} [props.className] - Optional custom class name
 */
const EntityDetailLayout = ({
  title,
  subtitle,
  icon,
  backLink,
  backText = 'Back',
  primaryActions = [],
  secondaryActions = [],
  titleAction,
  mainContent,
  resourcesContent,
  contextContent,
  relatedContent,
  notesContent,
  loading = false,
  error = null,
  className = ''
}) => {
  if (loading) {
    return (
      <div className="entity-detail-layout loading">
        <WaitingIndicator message={`Loading ${title || 'details'}...`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="entity-detail-layout error">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className={`entity-detail-layout ${className}`}>
      {/* Back Navigation (Optional) */}
      {backLink && (
        <div className="entity-back-nav">
          <Link to={backLink} className="back-link">
            <FaChevronLeft /> {backText}
          </Link>
        </div>
      )}

      {/* Header Section */}
      <header className="entity-header">
        {/* Historical .title-display structure for CSS compatibility */}
        <div className="title-display">
          <h1>
            {title}
            {subtitle && (
              <>
                <span className="context-separator">:</span>
                <span className="context-text">{subtitle}</span>
              </>
            )}
            {titleAction}
          </h1>

          {/* Buttons as siblings of h1 (historical pattern) */}
          {primaryActions.map((action, index) => (
            <React.Fragment key={`primary-${index}`}>
              {action}
            </React.Fragment>
          ))}
          {secondaryActions.map((action, index) => (
            <React.Fragment key={`secondary-${index}`}>
              {action}
            </React.Fragment>
          ))}
        </div>
      </header>

      {/* Add .main-content wrapper for historical CSS compatibility */}
      <div className="main-content">
        {/* Content Body */}
        <div className="entity-body">

          {/* Main Unique Content */}
          {mainContent && (
            <div className="entity-main-content">
              {mainContent}
            </div>
          )}

          {/* Resources Slot (New) */}
          {resourcesContent && (
            <section className="entity-resources-section">
              {resourcesContent}
            </section>
          )}

          {/* Context Sidebar (Optional) */}
          {contextContent && (
            <aside className="entity-context-sidebar">
              {contextContent}
            </aside>
          )}

          {/* Related Content (Unified) */}
          {relatedContent && (
            <div className="entity-related-section">
              {relatedContent}
            </div>
          )}

          {/* Notes Section (Standard Footer) */}
          {notesContent && (
            <div className="entity-notes-section">
              {notesContent}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

EntityDetailLayout.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subtitle: PropTypes.node,
  icon: PropTypes.node,
  backLink: PropTypes.string,
  backText: PropTypes.string,
  primaryActions: PropTypes.arrayOf(PropTypes.node),
  secondaryActions: PropTypes.arrayOf(PropTypes.node),
  titleAction: PropTypes.node,
  mainContent: PropTypes.node,
  resourcesContent: PropTypes.node,
  contextContent: PropTypes.node,
  relatedContent: PropTypes.node,
  notesContent: PropTypes.node,
  loading: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string
};

export default EntityDetailLayout;
